import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import {
  Button,
  Select,
  Row,
  Input,
  Table,
  Col,
  Checkbox,
  DatePicker,
} from 'antd';
import { useHistory } from 'react-router';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import startCase from 'lodash/startCase';

import GetField from '../Components/GetField';
import styles from '../styles';
import { successNotification, errorNotification } from '../Snackbar';
import Api from '../Api';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import Breadcrumbs from '../Components/Breadcrumbs';
import FabricForm from './FabricForm';
import AccessoryForm from './AccessoryForm';
import {
  clearRenukaPOIds,
  clearRpoInfo,
  clearRpoItemsForStoreOutward,
  clearVendors,
  clearRpoItemsForStoreOutwardTable,
  getRenukaPOIds,
  getRpoInfo,
  getRpoItemsForStoreOutward,
  getVendors,
  getRpoItemsForStoreOutwardTable,
  getColorIds,
  getUnitIds,
  clearColorIds,
  clearUnitIds,
  getFabricStocksForOutward,
  clearFabricStocksForOutward,
  clearAccessoriesStocksForOutward,
  getAccessoriesStocksForOutward,
  clearStoreOutwardsofRpo,
  getNewOutwardNumber,
} from '../Actions';

const AntInput = GetField(Input);
const AntSelect = GetField(Select);
const { Option } = Select;
const AntDatePicker = GetField(DatePicker);
const AntInputTextArea = GetField(Input.TextArea);
const AntCheckbox = GetField(Checkbox);

const CreateStoreOutward = props => {
  const { classes } = props;
  const history = useHistory();
  const dispatch = useDispatch();

  const { handleSubmit, control, getValues, watch, setValue } = useForm({
    shouldUnregister: false,
    defaultValues: {
      fabrics: [{ productId: null, fabricId: null }],
      accessories: [{ productId: null, accessoryId: null }],
      items: [{ name: null }],
      styles: [{ productId: null, styleId: null }],
    },
  });

  const {
    fields: fabricFields,
    append: fabricAppend,
    remove: fabricRemove,
  } = useFieldArray({
    control,
    name: 'fabrics',
  });

  const {
    fields: accessoryFields,
    append: accessoryAppend,
    remove: accessoryRemove,
  } = useFieldArray({
    control,
    name: 'accessories',
  });

  const { fields: itemFields } = useFieldArray({
    control,
    name: 'items',
  });

  const { fields: styleFields } = useFieldArray({
    control,
    name: 'styles',
  });

  const [confirmLoading, setConfirmLoading] = useState(false);

  const vendors = useSelector(state => state.vendors);
  const renukaPOIds = useSelector(state => state.renukaPOIds);
  const renukaPOInfo = useSelector(state => state.renukaPOInfo);
  const rpoItemsForStoreOutward = useSelector(
    state => state.rpoItemsForStoreOutward
  );
  const newOutwardNo = useSelector(state => state.newOutwardNo);

  useEffect(() => {
    dispatch(getNewOutwardNumber());
    dispatch(getVendors());
    dispatch(getRenukaPOIds({ isJobOrder: true }));
    dispatch(getColorIds());
    dispatch(getUnitIds());
    return () => {
      dispatch(clearStoreOutwardsofRpo());
      dispatch(clearVendors());
      dispatch(clearRenukaPOIds());
      dispatch(clearColorIds());
      dispatch(clearUnitIds());
      dispatch(clearRpoInfo());
      dispatch(clearRpoItemsForStoreOutward());
      dispatch(clearRpoItemsForStoreOutwardTable());
      dispatch(clearFabricStocksForOutward());
      dispatch(clearAccessoriesStocksForOutward());
    };
  }, []);

  useEffect(() => {
    const rpoId = getValues('rpoId');
    dispatch(clearRpoInfo());
    dispatch(clearRpoItemsForStoreOutward());
    dispatch(clearRpoItemsForStoreOutwardTable());
    dispatch(clearFabricStocksForOutward());
    dispatch(clearAccessoriesStocksForOutward());

    if (rpoId) {
      dispatch(getRpoInfo(rpoId));
      dispatch(getRpoItemsForStoreOutward(rpoId));
      dispatch(getRpoItemsForStoreOutwardTable(rpoId));
      dispatch(getFabricStocksForOutward(rpoId));
      dispatch(getAccessoriesStocksForOutward(rpoId));
    }
  }, [watch('rpoId')]);

  useEffect(() => {
    setValue('vendorId', renukaPOInfo.vendorId);
    setValue('vendorName', renukaPOInfo.vendor);
    setValue('vendorAddress', renukaPOInfo.vendorAddress);
    setValue('vendorState', renukaPOInfo.vendorState);
    setValue('vendorGstNo', renukaPOInfo.vendorGstNo);
    setValue('vendorMail', renukaPOInfo.vendorMail);
    setValue('vendorPhone', renukaPOInfo.vendorPhone);
  }, [renukaPOInfo]);

  useEffect(() => {
    setValue('items', rpoItemsForStoreOutward.items);
  }, [rpoItemsForStoreOutward.items]);

  useEffect(() => {
    setValue('styles', rpoItemsForStoreOutward.styles);
  }, [rpoItemsForStoreOutward.styles]);

  useEffect(() => {
    if (newOutwardNo) {
      setValue('newOutwardNo', newOutwardNo);
    }
  }, [newOutwardNo]);

  const fabricRowKey = useMemo(
    () => record => `P${record.productId}F${record.fabricId}C${record.colorId}`,
    []
  );

  const accessoryRowKey = useMemo(
    () => record => `P${record.productId}A${record.accessoryId}`,
    []
  );

  const itemRowKey = useMemo(
    () => record =>
      `N${record.name}S${record.shade}S${record.size}U${record.unitId}`,
    []
  );

  const styleRowKey = useMemo(
    () => record =>
      `P${record.productId}S${record.styleId}D${record.description}S${record.size}B${record.baseColorId}`,
    []
  );

  const scroll = useMemo(() => ({ x: 1400 }), []);

  const fabricsColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Sent',
        dataIndex: 'totalSent',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
    ],
    []
  );

  const accessoryColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Sent',
        dataIndex: 'totalSent',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
    ],
    []
  );

  const itemsColumns = useMemo(
    () => [
      {
        title: 'Item',
        dataIndex: 'name',
      },
      {
        title: 'Shade',
        dataIndex: 'shade',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Sent',
        dataIndex: 'totalSent',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
    ],
    []
  );

  const stylesColumns = useMemo(
    () => [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'style',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'RPO Rate',
        dataIndex: 'rpoRate',
      },
      {
        title: 'Total Sent',
        dataIndex: 'totalSent',
      },
      {
        title: 'Balance',
        dataIndex: 'balance',
      },
    ],
    []
  );

  const onSubmit = useCallback(async formValues => {
    try {
      setConfirmLoading(true);
      await Api.post('/store-outwards', formValues);
      successNotification('Store outward created');
      history.push('/storeoutward/list');
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, []);

  return (
    <>
      <Breadcrumbs
        moduleName="Store Outward"
        moduleLink="/storeoutward/list"
        subModuleName="Create"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <LeftGrid>
            <Controller
              name="vendorId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Vendor"
                  allowClear
                  placeholder="Select vendor"
                  {...field}
                  onChange={value => {
                    field.onChange(value);
                    setValue('rpoId', null);
                    setValue('fabrics', [{}]);
                    setValue('accessories', [{}]);
                    setValue('items', [{}]);
                    setValue('styles', [{}]);
                    dispatch(
                      getRenukaPOIds({ vendorId: value, isJobOrder: true })
                    );
                  }}
                >
                  {vendors.map(vendor => (
                    <Option key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="rpoId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="RPO #"
                  allowClear
                  placeholder="Select rpo"
                  {...field}
                >
                  {renukaPOIds.map(rpo => (
                    <Option key={rpo.id} value={rpo.id}>
                      {rpo.rpoNo}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="bpoNo"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="BPO #"
                  disabled
                  placeholder="BPO #"
                  {...{
                    ...field,
                    value: watch(`rpoId`),
                  }}
                >
                  {renukaPOIds.map(rpo => (
                    <Option key={rpo.id} value={rpo.id}>
                      {rpo.bpoNo}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="bpoNo"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Customer"
                  disabled
                  placeholder="Customer"
                  {...{
                    ...field,
                    value: watch(`rpoId`),
                  }}
                >
                  {renukaPOIds.map(rpo => (
                    <Option key={rpo.id} value={rpo.id}>
                      {rpo.customer}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="process"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Process"
                  disabled
                  placeholder="Process"
                  {...{
                    ...field,
                    value: watch(`rpoId`),
                  }}
                >
                  {renukaPOIds.map(rpo => (
                    <Option key={rpo.id} value={rpo.id}>
                      {startCase(rpo.process)}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="vendorAddress"
              control={control}
              render={({ field }) => (
                <AntInput
                  label="Vendor Address"
                  disabled
                  placeholder="Select RPO #"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="vendorState"
              control={control}
              render={({ field }) => (
                <AntInput
                  label="Vendor State"
                  disabled
                  placeholder="Select RPO #"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="vendorGstNo"
              control={control}
              render={({ field }) => (
                <AntInput
                  label="Vendor GST #"
                  disabled
                  placeholder="Select RPO #"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="vendorMail"
              control={control}
              render={({ field }) => (
                <AntInput
                  label="Vendor Email"
                  disabled
                  placeholder="Select RPO #"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="newOutwardNo"
              control={control}
              render={({ field }) => (
                <AntInput
                  label="Challan #"
                  disabled
                  placeholder="Challan #"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="outwardDate"
              control={control}
              render={({ field }) => (
                <AntDatePicker
                  label="Outward Date"
                  allowClear
                  placeholder="Outward Date"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="vehicleNo"
              control={control}
              render={({ field }) => (
                <AntInput
                  allowClear
                  label="Vehicle #"
                  placeholder="Vehicle #"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="isAlter"
              control={control}
              render={({ field }) => (
                <AntCheckbox label="Alter/Reprocess" {...field} />
              )}
            />
          </LeftGrid>
        </Row>
        <br />
        {watch('rpoId') ? (
          <div className={classes.previewMainHeading}>Job-Order Data Table</div>
        ) : null}
        <br />
        {rpoItemsForStoreOutward.items.length ? (
          <>
            <div className={classes.tableStyle}>
              <Table
                bordered
                size="small"
                columns={itemsColumns}
                dataSource={rpoItemsForStoreOutward.items}
                rowKey={itemRowKey}
                pagination={false}
                scroll={scroll}
                title={() => 'Items Table'}
              />
            </div>
            <br />
          </>
        ) : null}
        {rpoItemsForStoreOutward.fabrics.length ? (
          <>
            <div className={classes.tableStyle}>
              <Table
                bordered
                size="small"
                columns={fabricsColumns}
                dataSource={rpoItemsForStoreOutward.fabrics}
                rowKey={fabricRowKey}
                pagination={false}
                scroll={scroll}
                title={() => 'Fabrics Table'}
              />
            </div>
            <br />
          </>
        ) : null}
        {rpoItemsForStoreOutward.accessories.length ? (
          <>
            <div className={classes.tableStyle}>
              <Table
                bordered
                size="small"
                columns={accessoryColumns}
                dataSource={rpoItemsForStoreOutward.accessories}
                rowKey={accessoryRowKey}
                pagination={false}
                scroll={scroll}
                title={() => 'Accessories Table'}
              />
            </div>
            <br />
          </>
        ) : null}
        {rpoItemsForStoreOutward.styles.length ? (
          <>
            <div className={classes.tableStyle}>
              <Table
                bordered
                size="small"
                columns={stylesColumns}
                dataSource={rpoItemsForStoreOutward.styles}
                rowKey={styleRowKey}
                pagination={false}
                scroll={scroll}
                title={() => 'Products Table'}
              />
            </div>
            <br />
          </>
        ) : null}
        {watch('rpoId') ? (
          <>
            <br />
            <br />
            <div className={classes.previewMainHeading}>
              Store Outward Input Table
            </div>
          </>
        ) : null}
        <br />
        {rpoItemsForStoreOutward.items.length ? (
          <>
            <div className={classes.previewSubHeading}>Items Table</div>
            <Row>
              <Col className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr>
                      <th className={classes.inputTh} style={{ width: '40%' }}>
                        Item
                      </th>
                      <th className={classes.inputTh} style={{ width: '17%' }}>
                        Shade
                      </th>
                      <th className={classes.inputTh} style={{ width: '10%' }}>
                        Size
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Unit
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Quantity
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Rate
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        GST(%)
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Packages
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemFields.map((item, index) => (
                      <tr key={item.id}>
                        <td className={classes.inputTd}>{item.name}</td>
                        <td className={classes.inputTd}>{item.shade}</td>
                        <td className={classes.inputTd}>{item.size}</td>
                        <td className={classes.inputTd}>{item.unit}</td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`items[${index}].quantity`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Qty"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`items[${index}].rate`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Rate"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`items[${index}].gst`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Gst"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`items[${index}].packages`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="Package" {...field} />
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            </Row>
            <br />
          </>
        ) : null}
        {rpoItemsForStoreOutward.fabrics.length ? (
          <>
            <div className={classes.previewSubHeading}>Fabrics Table</div>
            <Row>
              <div className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr key="header">
                      {renukaPOInfo.bpoNo ? (
                        <th
                          className={classes.inputTh}
                          style={{ width: '10%' }}
                        >
                          Product
                        </th>
                      ) : null}
                      <th className={classes.inputTh}>Fabric</th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Width
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        From Base Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        From Print Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Target Base Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Target Print Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Factory
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Rack #
                      </th>
                      <th className={classes.inputTh} style={{ width: '5%' }}>
                        Inventory Qty
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Quantity
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Rate
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        GST(%)
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Packages
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {fabricFields.map((item, index) => (
                      <FabricForm
                        key={item.id}
                        fabricItem={item}
                        fabricIndex={index}
                        fabricAppend={fabricAppend}
                        fabricRemove={fabricRemove}
                        getValues={getValues}
                        setValue={setValue}
                        control={control}
                        watch={watch}
                        bpoNo={renukaPOInfo.bpoNo}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </Row>
            <br />
          </>
        ) : null}
        {rpoItemsForStoreOutward.accessories.length ? (
          <>
            <div className={classes.previewSubHeading}>Accessories Table</div>
            <Row>
              <div className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr key="header">
                      {renukaPOInfo.bpoNo ? (
                        <th
                          className={classes.inputTh}
                          style={{ width: '15%' }}
                        >
                          Product
                        </th>
                      ) : null}
                      <th className={classes.inputTh}>Accessory</th>
                      <th className={classes.inputTh} style={{ width: '4%' }}>
                        Unit
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Factory
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Rack #
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Inventory Qty
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Quantity
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Rate
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        GST(%)
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Packages
                      </th>
                      <th className={classes.inputTh} style={{ width: '6%' }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessoryFields.map((item, index) => (
                      <AccessoryForm
                        key={item.id}
                        accessoryItem={item}
                        accessoryIndex={index}
                        accessoryAppend={accessoryAppend}
                        accessoryRemove={accessoryRemove}
                        getValues={getValues}
                        setValue={setValue}
                        control={control}
                        watch={watch}
                        bpoNo={renukaPOInfo.bpoNo}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </Row>
            <br />
          </>
        ) : null}
        {rpoItemsForStoreOutward.styles.length ? (
          <>
            <div className={classes.previewSubHeading}>Products Table</div>
            <Row>
              <Col className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr>
                      <th className={classes.inputTh} style={{ width: '10%' }}>
                        Product
                      </th>
                      <th className={classes.inputTh} style={{ width: '10%' }}>
                        Style
                      </th>
                      <th className={classes.inputTh} style={{ width: '10%' }}>
                        Description
                      </th>
                      <th className={classes.inputTh} style={{ width: '10%' }}>
                        Size
                      </th>
                      <th className={classes.inputTh} style={{ width: '8%' }}>
                        Base Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '8%' }}>
                        Print Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '8%' }}>
                        Embroidery Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '8%' }}>
                        Acid Wash Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Quantity
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Front
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Back
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Rate
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        GST(%)
                      </th>
                      <th className={classes.inputTh} style={{ width: '7%' }}>
                        Packages
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {styleFields.map((item, index) => (
                      <tr key={item.id}>
                        <td className={classes.inputTd}>{item.product}</td>
                        <td className={classes.inputTd}>{item.style}</td>
                        <td className={classes.inputTd}>{item.description}</td>
                        <td className={classes.inputTd}>{item.size}</td>
                        <td className={classes.inputTd}>{item.baseColor}</td>
                        <td className={classes.inputTd}>{item.printColor}</td>
                        <td className={classes.inputTd}>
                          {item.embroideryColor}
                        </td>
                        <td className={classes.inputTd}>
                          {item.acidWashColor}
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].quantity`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Qty"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].frontQuantity`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Front (If required)"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].backQuantity`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Back (If required)"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].rate`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Rate"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].gst`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                type="number"
                                size="small"
                                min={0}
                                placeholder="Gst"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].packages`}
                            control={control}
                            render={({ field }) => (
                              <AntInput placeholder="Package" {...field} />
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            </Row>
            <br />
          </>
        ) : null}
        <Row>
          <LeftGrid>
            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <AntInputTextArea
                  label="Remark"
                  allowClear
                  placeholder="Remark"
                  {...field}
                />
              )}
            />
          </LeftGrid>
        </Row>
        <Row className={classes.buttonContainer}>
          <Button type="primary" loading={confirmLoading} htmlType="submit">
            Submit
          </Button>
        </Row>
      </form>
    </>
  );
};

CreateStoreOutward.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CreateStoreOutward);
